// TaskBatchCreator.jsx - Complete working version
import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Table,
  Badge,
  Alert,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import { saveTaskDetails, getContactGuid } from "../../Api/TaskMaster";
import { getAllTaskStatus, getAllTaskTypes } from "../../Api/TaskMaster";
import { ticketDropdownApi } from "../../Api/TicketApiOrg";
import { fetchStagesByProject } from "../../Api/StageMaster";

const TaskBatchCreator = ({
  show,
  onHide,
  projectId,
  orgId,
  userId,
  teamMembers,
  onSuccess,
  existingTasks = [],
}) => {
  const [tasks, setTasks] = useState([]);
  const [selectedTaskIndices, setSelectedTaskIndices] = useState(new Set());
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [bulkValues, setBulkValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [duplicateDetection, setDuplicateDetection] = useState(true);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteData, setPasteData] = useState("");
  const [previewRows, setPreviewRows] = useState([]);
  
  // Dynamic data states (same as TaskMaster)
  const [assignedToOptions, setAssignedToOptions] = useState([]);
  const [loadingAssignedTo, setLoadingAssignedTo] = useState(false);
  const [taskTypes, setTaskTypes] = useState([]);
  const [loadingTaskTypes, setLoadingTaskTypes] = useState(false);
  const [statusList, setStatusList] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [stages, setStages] = useState([]);
  const [loadingStages, setLoadingStages] = useState(false);
  
  const [selectedColumns, setSelectedColumns] = useState({
    taskName: null,
    taskDescription: null,
    assignedTo: null,
    taskStartDate: null,
    taskEndDate: null,
    taskPriority: null,
    taskStatus: null,
    taskType: null,
    stageBelong: null,
  });

  const hasInitialized = useRef(false);
  const username = JSON.parse(localStorage.getItem("userDetails"))?.username || "";

  // Priority options (hardcoded as they're standard)
  const priorities = [
    { value: "Low", label: "Low" },
    { value: "Medium", label: "Medium" },
    { value: "High", label: "High" },
    { value: "Critical", label: "Critical" }
  ];

  // Load assignedTo options from ticketDropdownApi (same as TaskMaster)
  const loadAssignedToOptions = async () => {
    if (!orgId || !projectId) {
      setAssignedToOptions([]);
      return;
    }

    setLoadingAssignedTo(true);
    try {
      const [team, customers, partners] = await Promise.all([
        ticketDropdownApi.getTeamAssigned(orgId, projectId),
        ticketDropdownApi.getCustomer(orgId, projectId),
        ticketDropdownApi.getPartner(orgId, projectId),
      ]);

      const assigneeMap = new Map();

      team.forEach((item) => {
        if (!assigneeMap.has(item.contactGUID)) {
          assigneeMap.set(item.contactGUID, {
            value: item.contactGUID,
            label: `${item.contactName} (Team)`,
            type: "Team",
            contactName: item.contactName,
          });
        }
      });

      customers.forEach((item) => {
        if (!assigneeMap.has(item.contactGUID)) {
          assigneeMap.set(item.contactGUID, {
            value: item.contactGUID,
            label: `${item.contactName} (Customer)`,
            type: "Customer",
            contactName: item.contactName,
          });
        }
      });

      partners.forEach((item) => {
        if (!assigneeMap.has(item.contactGUID)) {
          assigneeMap.set(item.contactGUID, {
            value: item.contactGUID,
            label: `${item.contactName} (Partner)`,
            type: "Partner",
            contactName: item.contactName,
          });
        }
      });

      const sortedOptions = Array.from(assigneeMap.values()).sort((a, b) =>
        a.label.localeCompare(b.label)
      );

      setAssignedToOptions(sortedOptions);
    } catch (error) {
      console.error("Error loading assigned to options:", error);
      setAssignedToOptions([]);
    } finally {
      setLoadingAssignedTo(false);
    }
  };

  // Load task types from API
  const loadTaskTypes = async () => {
    try {
      setLoadingTaskTypes(true);
      const data = await getAllTaskTypes();
      const sorted = data.sort((a, b) => a.typeName.localeCompare(b.typeName));
      setTaskTypes(sorted);
    } catch (error) {
      console.error("Error loading task types:", error);
      setTaskTypes([]);
    } finally {
      setLoadingTaskTypes(false);
    }
  };

  // Load task statuses from API
  const loadTaskStatus = async () => {
    try {
      setLoadingStatus(true);
      const data = await getAllTaskStatus();
      const sorted = data.sort((a, b) => a.statusName.localeCompare(b.statusName));
      setStatusList(sorted);
    } catch (error) {
      console.error("Error loading status:", error);
      setStatusList([]);
    } finally {
      setLoadingStatus(false);
    }
  };

  // Load stages using the correct API function
  const loadStages = async () => {
    if (!projectId) {
      setStages([]);
      return;
    }
    
    setLoadingStages(true);
    try {
      const stagesData = await fetchStagesByProject(projectId);
      console.log("Loaded stages:", stagesData);
      setStages(stagesData || []);
    } catch (error) {
      console.error("Error loading stages:", error);
      setStages([]);
    } finally {
      setLoadingStages(false);
    }
  };

  // Load all dynamic data when modal opens or projectId changes
  useEffect(() => {
    if (show && projectId) {
      loadAssignedToOptions();
      loadTaskTypes();
      loadTaskStatus();
      loadStages();
    }
  }, [show, projectId, orgId]);

  const handleClose = () => {
    setTasks([]);
    setSelectedTaskIndices(new Set());
    setBulkEditMode(false);
    setBulkValues({});
    setShowValidation(false);
    setShowPasteModal(false);
    setPasteData("");
    setPreviewRows([]);
    setSelectedColumns({
      taskName: null,
      taskDescription: null,
      assignedTo: null,
      taskStartDate: null,
      taskEndDate: null,
      taskPriority: null,
      taskStatus: null,
      taskType: null,
      stageBelong: null,
    });
    hasInitialized.current = false;
    onHide();
  };

  useEffect(() => {
    if (show && !hasInitialized.current) {
      hasInitialized.current = true;
      if (existingTasks.length > 0) {
        const clonedTasks = existingTasks.map((task) => ({
          id: `temp_${Date.now()}_${Math.random()}`,
          taskName: `${task.taskName} (Copy)`,
          taskDescription: task.taskDescription || "",
          assignedTo: task.assignedTo || "",
          taskStartDate: task.taskStartDate || "",
          taskEndDate: task.taskEndDate || "",
          taskPriority: task.taskPriority || "Medium",
          taskStatus: task.taskStatus || "",
          taskType: task.taskType || "",
          stageBelong: task.stageBelong || "",
          errors: {},
        }));
        setTasks(clonedTasks);
      } else {
        addNewTasks(3);
      }
    }
    if (!show) {
      hasInitialized.current = false;
    }
  }, [show, existingTasks]);

  const parsePastedData = (text) => {
    const lines = text.split(/\r?\n/).filter(line => line.trim());
    const data = lines.map(line => {
      const delimiter = line.includes('\t') ? '\t' : ',';
      return line.split(delimiter).map(cell => cell.trim());
    });
    return data;
  };

  const handleParsePaste = () => {
    if (!pasteData.trim()) {
      toast.error("Please paste data first");
      return;
    }

    const parsedData = parsePastedData(pasteData);
    if (parsedData.length === 0) {
      toast.error("No valid data found");
      return;
    }

    setPreviewRows(parsedData);
    toast.success(`Found ${parsedData.length} rows of data. Select which columns to use.`);
  };

  const addTasksFromPaste = () => {
    if (previewRows.length === 0) return;

    if (selectedColumns.taskName === null || selectedColumns.taskName === undefined) {
      toast.error("Please select a column for Task Name (required field)");
      return;
    }

    const newTasks = previewRows.slice(1).map(row => {
      const taskName = selectedColumns.taskName !== null ? row[selectedColumns.taskName] || "" : "";
      const taskDescription = selectedColumns.taskDescription !== null ? row[selectedColumns.taskDescription] || "" : "";
      let assignedTo = selectedColumns.assignedTo !== null ? row[selectedColumns.assignedTo] || "" : "";
      const taskStartDate = selectedColumns.taskStartDate !== null ? row[selectedColumns.taskStartDate] || "" : "";
      const taskEndDate = selectedColumns.taskEndDate !== null ? row[selectedColumns.taskEndDate] || "" : "";
      let taskPriority = selectedColumns.taskPriority !== null ? row[selectedColumns.taskPriority] || "Medium" : "Medium";
      let taskStatus = selectedColumns.taskStatus !== null ? row[selectedColumns.taskStatus] || "" : "";
      let taskType = selectedColumns.taskType !== null ? row[selectedColumns.taskType] || "" : "";
      let stageBelong = selectedColumns.stageBelong !== null ? row[selectedColumns.stageBelong] || "" : "";

      if (!taskName) return null;

      // Try to find matching assignee by name
      if (assignedTo && assignedToOptions.length > 0) {
        const matchedOption = assignedToOptions.find(opt => 
          opt.contactName?.toLowerCase() === assignedTo.toLowerCase() ||
          opt.label?.toLowerCase().includes(assignedTo.toLowerCase())
        );
        if (matchedOption) {
          assignedTo = matchedOption.value;
        }
      }

      // Try to find matching task type by name
      if (taskType && taskTypes.length > 0) {
        const matchedType = taskTypes.find(t => 
          t.typeName?.toLowerCase() === taskType.toLowerCase()
        );
        if (matchedType) {
          taskType = matchedType.typeGuid;
        }
      }

      // Try to find matching status by name
      if (taskStatus && statusList.length > 0) {
        const matchedStatus = statusList.find(s => 
          s.statusName?.toLowerCase() === taskStatus.toLowerCase()
        );
        if (matchedStatus) {
          taskStatus = matchedStatus.statusGuid;
        }
      }

      // Try to find matching stage by name
      if (stageBelong && stages.length > 0) {
        const matchedStage = stages.find(s => 
          s.stageName?.toLowerCase() === stageBelong.toLowerCase()
        );
        if (matchedStage) {
          stageBelong = matchedStage.stageId;
        }
      }

      // Validate priority
      const validPriorities = priorities.map(p => p.value);
      if (!validPriorities.includes(taskPriority)) {
        taskPriority = "Medium";
      }

      return {
        id: `temp_${Date.now()}_${Math.random()}`,
        taskName: taskName,
        taskDescription: taskDescription,
        assignedTo: assignedTo,
        taskStartDate: taskStartDate,
        taskEndDate: taskEndDate,
        taskPriority: taskPriority,
        taskStatus: taskStatus,
        taskType: taskType,
        stageBelong: stageBelong,
        errors: {},
      };
    }).filter(task => task !== null);

    if (newTasks.length === 0) {
      toast.error("No valid tasks found. Make sure Task Name column has data.");
      return;
    }

    setTasks(prev => [...prev, ...newTasks]);
    setShowPasteModal(false);
    setPasteData("");
    setPreviewRows([]);
    toast.success(`Added ${newTasks.length} tasks from Excel`);
  };

  const addNewTasks = (count = 1) => {
    const newTasks = Array(count).fill().map(() => ({
      id: `temp_${Date.now()}_${Math.random()}`,
      taskName: "",
      taskDescription: "",
      assignedTo: "",
      taskStartDate: "",
      taskEndDate: "",
      taskPriority: "Medium",
      taskStatus: "",
      taskType: "",
      stageBelong: "",
      errors: {},
    }));
    setTasks((prev) => [...prev, ...newTasks]);
  };

  const removeTask = (index) => {
    setTasks((prev) => prev.filter((_, i) => i !== index));
    setSelectedTaskIndices((prev) => {
      const newSet = new Set();
      prev.forEach((i) => {
        if (i < index) newSet.add(i);
        else if (i > index) newSet.add(i - 1);
      });
      return newSet;
    });
  };

  const updateTask = (index, field, value) => {
    setTasks((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
        errors: {
          ...updated[index].errors,
          [field]: validateField(field, value, updated[index]),
        },
      };
      return updated;
    });
  };

  const validateField = (field, value, task) => {
    switch (field) {
      case "taskName":
        if (!value || !value.trim()) return "Task name is required";
        if (duplicateDetection && checkDuplicateName(value, task.id)) {
          return "Duplicate task name detected";
        }
        return null;
      case "taskStartDate":
        if (value && task.taskEndDate && new Date(value) > new Date(task.taskEndDate)) {
          return "Start date cannot be after end date";
        }
        return null;
      case "taskEndDate":
        if (value && task.taskStartDate && new Date(value) < new Date(task.taskStartDate)) {
          return "End date cannot be before start date";
        }
        return null;
      default:
        return null;
    }
  };

  const checkDuplicateName = (name, currentId) => {
    return tasks.some(
      (t) => t.id !== currentId && t.taskName?.toLowerCase() === name.toLowerCase()
    );
  };

  const applyBulkUpdate = () => {
    if (selectedTaskIndices.size === 0) return;

    setTasks((prev) => {
      const updated = [...prev];
      selectedTaskIndices.forEach((index) => {
        updated[index] = {
          ...updated[index],
          ...bulkValues,
          errors: {
            ...updated[index].errors,
            ...Object.keys(bulkValues).reduce((acc, field) => {
              acc[field] = validateField(field, bulkValues[field], updated[index]);
              return acc;
            }, {}),
          },
        };
      });
      return updated;
    });

    setBulkEditMode(false);
    setBulkValues({});
    toast.success(`Updated ${selectedTaskIndices.size} tasks`);
  };

  const toggleTaskSelection = (index) => {
    setSelectedTaskIndices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) newSet.delete(index);
      else newSet.add(index);
      return newSet;
    });
  };

  const selectAllTasks = () => {
    if (selectedTaskIndices.size === tasks.length) {
      setSelectedTaskIndices(new Set());
    } else {
      setSelectedTaskIndices(new Set(tasks.map((_, i) => i)));
    }
  };

  const validateAllTasks = () => {
    let isValid = true;
    const updatedTasks = [...tasks];

    updatedTasks.forEach((task, index) => {
      const errors = {};
      if (!task.taskName?.trim()) {
        errors.taskName = "Task name is required";
        isValid = false;
      }
      if (task.taskStartDate && task.taskEndDate) {
        if (new Date(task.taskStartDate) > new Date(task.taskEndDate)) {
          errors.taskEndDate = "End date must be after start date";
          isValid = false;
        }
      }
      updatedTasks[index].errors = errors;
    });

    setTasks(updatedTasks);
    setShowValidation(true);

    if (!isValid) {
      toast.error("Please fix validation errors before saving");
    }
    return isValid;
  };

  // FIXED: Handle save with Contact GUID (same as TaskMaster)
  const handleSave = async () => {
    if (!validateAllTasks()) return;

    setSaving(true);
    const validTasks = tasks.filter((task) => task.taskName?.trim());

    try {
      // 🔥 STEP 1: Get Contact GUID (same as TaskMaster)
      const contactResponse = await getContactGuid(orgId, userId);

      if (!contactResponse || !contactResponse.contactGuid) {
        toast.error("Unable to fetch contact GUID");
        setSaving(false);
        return;
      }

      const contactGuid = contactResponse.contactGuid;
      console.log("Fetched Contact GUID:", contactGuid);

      const createdTasks = [];
      const errors = [];

      // 🔥 STEP 2: Create each task with the contact GUID
      for (const task of validTasks) {
        try {
          // Prepare task data matching TaskMaster structure
          const taskData = {
            orgId: String(orgId),
            taskName: task.taskName,
            taskDescription: task.taskDescription || "",
            assignedTo: task.assignedTo || null,
            taskStartDate: task.taskStartDate || null,
            taskEndDate: task.taskEndDate || null,
            taskPriority: task.taskPriority || "Medium",
            taskStatus: task.taskStatus || null,
            taskType: task.taskType || null,
            stageBelong: task.stageBelong || null,
            projectBelong: projectId,
            createdBy: username || String(userId),
            editedBy: username || String(userId),
            // 🔥 IMPORTANT: Add TaskCreatedGuid (Contact GUID)
            TaskCreatedGuid: contactGuid,
          };

          // Remove undefined/null values to clean up the payload
          Object.keys(taskData).forEach(key => {
            if (taskData[key] === null || taskData[key] === undefined) {
              delete taskData[key];
            }
          });

          console.log("Creating task with data:", taskData);

          const response = await saveTaskDetails(taskData, username || userId);
          createdTasks.push(response);
        } catch (error) {
          console.error("Error creating task:", error);
          errors.push({ 
            task: task.taskName, 
            error: error.response?.data?.title || error.message 
          });
        }
      }

      if (errors.length > 0) {
        toast.warning(`Created ${createdTasks.length} tasks, ${errors.length} failed`);
        console.error("Failed tasks:", errors);
      } else {
        toast.success(`Successfully created ${createdTasks.length} tasks`);
      }

      onSuccess?.(createdTasks);
      handleClose();
    } catch (error) {
      console.error("Error saving tasks:", error);
      toast.error(error.response?.data?.title || "Failed to save tasks");
    } finally {
      setSaving(false);
    }
  };

  const renderColumnSelect = (field, label, required = false) => {
    const columnCount = previewRows[0]?.length || 0;
    
    return (
      <Form.Group>
        <Form.Label>
          {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
        <Form.Select 
          size="sm" 
          value={selectedColumns[field] !== null ? selectedColumns[field] : ""}
          onChange={(e) => {
            const value = e.target.value === "" ? null : parseInt(e.target.value);
            setSelectedColumns({...selectedColumns, [field]: value});
          }}
        >
          <option value="">-- None (Skip Column) --</option>
          {Array.from({ length: columnCount }, (_, idx) => (
            <option key={idx} value={idx}>
              Column {idx + 1}: {previewRows[0][idx]?.substring(0, 40) || `Column ${idx + 1}`}
            </option>
          ))}
        </Form.Select>
        {required && selectedColumns[field] === null && (
          <Form.Text className="text-danger">
            This field is required
          </Form.Text>
        )}
      </Form.Group>
    );
  };

  // Helper function to get display name for GUID values
  const getAssignedToDisplayName = (value) => {
    if (!value) return "Unassigned";
    const option = assignedToOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const getTaskTypeDisplayName = (value) => {
    if (!value) return "Not selected";
    const type = taskTypes.find(t => t.typeGuid === value);
    return type ? type.typeName : value;
  };

  const getTaskStatusDisplayName = (value) => {
    if (!value) return "Not selected";
    const status = statusList.find(s => s.statusGuid === value);
    return status ? status.statusName : value;
  };

  const getStageDisplayName = (value) => {
    if (!value) return "Not selected";
    const stage = stages.find(s => s.stageId === value);
    return stage ? stage.stageName : value;
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} size="xl" centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-layer-group me-2"></i>
            Bulk Task Creator
            <Badge bg="info" className="ms-2">
              {tasks.length} Tasks
            </Badge>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Toolbar */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="btn-group">
                <Button size="sm" variant="outline-primary" onClick={() => addNewTasks(1)}>
                  <i className="fas fa-plus me-1"></i> Add Task
                </Button>
                <Button size="sm" variant="outline-success" onClick={() => addNewTasks(5)}>
                  <i className="fas fa-plus-circle me-1"></i> Add 5
                </Button>
                <Button size="sm" variant="outline-info" onClick={() => setShowPasteModal(true)}>
                  <i className="fas fa-paste me-1"></i> Paste from Excel
                </Button>
                <Button size="sm" variant="outline-danger" onClick={() => {
                  if (window.confirm("Remove all empty tasks?")) {
                    setTasks(tasks.filter((t) => t.taskName?.trim()));
                  }
                }}>
                  <i className="fas fa-trash me-1"></i> Clean Empty
                </Button>
              </div>

              <div className="btn-group">
                <Button size="sm" variant={selectedTaskIndices.size > 0 ? "primary" : "outline-secondary"} onClick={selectAllTasks}>
                  <i className="fas fa-check-square me-1"></i>
                  {selectedTaskIndices.size === tasks.length ? "Deselect All" : "Select All"}
                </Button>
                {selectedTaskIndices.size > 0 && (
                  <Button size="sm" variant="warning" onClick={() => setBulkEditMode(true)}>
                    <i className="fas fa-edit me-1"></i>
                    Bulk Edit ({selectedTaskIndices.size})
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Bulk Edit Panel */}
          {bulkEditMode && (
            <Alert variant="info" className="mb-3">
              <Alert.Heading className="h6">Bulk Edit {selectedTaskIndices.size} Tasks</Alert.Heading>
              <Row className="g-2">
                <Col md={3}>
                  <Form.Select size="sm" value={bulkValues.taskPriority || ""} onChange={(e) =>
                    setBulkValues({ ...bulkValues, taskPriority: e.target.value })}>
                    <option value="">Set Priority...</option>
                    {priorities.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Select size="sm" value={bulkValues.taskStatus || ""} onChange={(e) =>
                    setBulkValues({ ...bulkValues, taskStatus: e.target.value })}>
                    <option value="">Set Status...</option>
                    {statusList.map(s => <option key={s.statusGuid} value={s.statusGuid}>{s.statusName}</option>)}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Select size="sm" value={bulkValues.taskType || ""} onChange={(e) =>
                    setBulkValues({ ...bulkValues, taskType: e.target.value })}>
                    <option value="">Set Task Type...</option>
                    {taskTypes.map(t => <option key={t.typeGuid} value={t.typeGuid}>{t.typeName}</option>)}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Select size="sm" value={bulkValues.assignedTo || ""} onChange={(e) =>
                    setBulkValues({ ...bulkValues, assignedTo: e.target.value })}>
                    <option value="">Assign To...</option>
                    <option value="unassigned">Unassigned</option>
                    {assignedToOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
              <Row className="g-2 mt-2">
                <Col md={3}>
                  <Form.Select size="sm" value={bulkValues.stageBelong || ""} onChange={(e) =>
                    setBulkValues({ ...bulkValues, stageBelong: e.target.value })}>
                    <option value="">Set Stage...</option>
                    {stages.map(s => <option key={s.stageId} value={s.stageId}>{s.stageName}</option>)}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <div className="d-flex gap-2">
                    <Button size="sm" variant="primary" onClick={applyBulkUpdate}>Apply</Button>
                    <Button size="sm" variant="secondary" onClick={() => setBulkEditMode(false)}>Cancel</Button>
                  </div>
                </Col>
              </Row>
            </Alert>
          )}

          {/* Tasks Table */}
          <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
            <Table bordered hover className="mb-0">
              <thead className="table-light sticky-top">
                <tr>
                  <th style={{ width: "30px" }}>
                    <Form.Check type="checkbox" checked={selectedTaskIndices.size === tasks.length && tasks.length > 0} onChange={selectAllTasks} />
                  </th>
                  <th style={{ width: "30px" }}>#</th>
                  <th style={{ width: "15%" }}>Task Name *</th>
                  <th style={{ width: "20%" }}>Description</th>
                  <th style={{ width: "12%" }}>Assignee</th>
                  <th style={{ width: "8%" }}>Stage</th>
                  <th style={{ width: "8%" }}>Start Date</th>
                  <th style={{ width: "8%" }}>End Date</th>
                  <th style={{ width: "8%" }}>Priority</th>
                  <th style={{ width: "8%" }}>Status</th>
                  <th style={{ width: "8%" }}>Task Type</th>
                  <th style={{ width: "50px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task, index) => (
                  <tr key={task.id} style={{ backgroundColor: task.errors.taskName ? "#fff3f3" : "white" }}>
                    <td className="text-center">
                      <Form.Check type="checkbox" checked={selectedTaskIndices.has(index)} onChange={() => toggleTaskSelection(index)} />
                    </td>
                    <td className="text-muted">{index + 1}</td>
                    <td>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={task.taskName}
                        onChange={(e) => updateTask(index, "taskName", e.target.value)}
                        placeholder="Enter task name"
                        isInvalid={!!task.errors.taskName}
                      />
                      {task.errors.taskName && (
                        <Form.Control.Feedback type="invalid" className="small">
                          {task.errors.taskName}
                        </Form.Control.Feedback>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={task.taskDescription}
                        onChange={(e) => updateTask(index, "taskDescription", e.target.value)}
                        placeholder="Optional description"
                      />
                    </td>
                    <td>
                      <Form.Select 
                        size="sm" 
                        value={task.assignedTo || ""} 
                        onChange={(e) => updateTask(index, "assignedTo", e.target.value)}
                        disabled={loadingAssignedTo}
                      >
                        <option value="">Unassigned</option>
                        {assignedToOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Select 
                        size="sm" 
                        value={task.stageBelong || ""} 
                        onChange={(e) => updateTask(index, "stageBelong", e.target.value)}
                        disabled={loadingStages}
                      >
                        <option value="">Select Stage</option>
                        {stages.map(s => (
                          <option key={s.stageId} value={s.stageId}>{s.stageName}</option>
                        ))}
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Control 
                        type="date" 
                        size="sm" 
                        value={task.taskStartDate} 
                        onChange={(e) => updateTask(index, "taskStartDate", e.target.value)} 
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="date"
                        size="sm"
                        value={task.taskEndDate}
                        onChange={(e) => updateTask(index, "taskEndDate", e.target.value)}
                        isInvalid={!!task.errors.taskEndDate}
                      />
                      {task.errors.taskEndDate && (
                        <Form.Control.Feedback type="invalid" className="small">
                          {task.errors.taskEndDate}
                        </Form.Control.Feedback>
                      )}
                    </td>
                    <td>
                      <Form.Select 
                        size="sm" 
                        value={task.taskPriority} 
                        onChange={(e) => updateTask(index, "taskPriority", e.target.value)}
                      >
                        {priorities.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Select 
                        size="sm" 
                        value={task.taskStatus || ""} 
                        onChange={(e) => updateTask(index, "taskStatus", e.target.value)}
                        disabled={loadingStatus}
                      >
                        <option value="">Select Status</option>
                        {statusList.map(s => (
                          <option key={s.statusGuid} value={s.statusGuid}>{s.statusName}</option>
                        ))}
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Select 
                        size="sm" 
                        value={task.taskType || ""} 
                        onChange={(e) => updateTask(index, "taskType", e.target.value)}
                        disabled={loadingTaskTypes}
                      >
                        <option value="">Select Type</option>
                        {taskTypes.map(t => (
                          <option key={t.typeGuid} value={t.typeGuid}>{t.typeName}</option>
                        ))}
                      </Form.Select>
                    </td>
                    <td className="text-center">
                      <Button variant="link" size="sm" className="text-danger p-0" onClick={() => removeTask(index)}>
                        <i className="fas fa-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Quick Actions */}
          <div className="mt-3">
            <div className="d-flex gap-2">
              <Button size="sm" variant="outline-secondary" onClick={() => {
                const template = {
                  taskName: "New Task",
                  taskDescription: "",
                  assignedTo: "",
                  taskStartDate: moment().format("YYYY-MM-DD"),
                  taskEndDate: moment().add(7, "days").format("YYYY-MM-DD"),
                  taskPriority: "Medium",
                  taskStatus: statusList[0]?.statusGuid || "",
                  taskType: taskTypes[0]?.typeGuid || "",
                  stageBelong: stages[0]?.stageId || "",
                };
                setTasks(prev => [...prev, { ...template, id: `temp_${Date.now()}` }]);
              }}>
                <i className="fas fa-file-alt me-1"></i> Add from Template
              </Button>

              <Form.Check
                type="switch"
                label="Detect Duplicates"
                checked={duplicateDetection}
                onChange={(e) => setDuplicateDetection(e.target.checked)}
                className="ms-auto"
              />
            </div>
          </div>

          {/* Validation Summary */}
          {showValidation && tasks.some(t => Object.keys(t.errors).length > 0) && (
            <Alert variant="danger" className="mt-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <strong>Validation Errors Found:</strong>
              <ul className="mb-0 mt-2">
                {tasks.map((task, idx) =>
                  Object.entries(task.errors).map(([field, error]) => 
                    error && <li key={`${idx}-${field}`}>Task {idx + 1}: {error}</li>
                  )
                )}
              </ul>
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <div>
              <span className="text-muted">
                <i className="fas fa-info-circle me-1"></i>
                Select multiple tasks for bulk updates | 📋 Paste from Excel
              </span>
            </div>
            <div>
              <Button variant="secondary" onClick={handleClose} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving || tasks.filter(t => t.taskName?.trim()).length === 0}
                className="ms-2"
              >
                {saving ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2"></i>
                    Create {tasks.filter(t => t.taskName?.trim()).length} Tasks
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Paste from Excel Modal */}
      <Modal show={showPasteModal} onHide={() => setShowPasteModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-paste me-2 text-success"></i>
            Paste from Excel
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <strong>How to use:</strong>
            <ol className="mb-0 mt-2">
              <li>Copy data from Excel (Ctrl+C / Cmd+C)</li>
              <li>Paste below (Ctrl+V / Cmd+V)</li>
              <li>Click "Parse & Preview"</li>
              <li>Select which column corresponds to each field (use "None" to skip)</li>
              <li>Click "Add Tasks" to import</li>
            </ol>
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label>Paste Excel Data Here:</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              value={pasteData}
              onChange={(e) => setPasteData(e.target.value)}
              placeholder="Example:&#10;Task Name	Description	Assignee	Start Date	End Date	Priority	Status	Task Type	Stage&#10;Task 1	Test task	Rohity	2024-01-01	2024-01-31	High	Not Started	Development	Sprint 1"
            />
          </Form.Group>

          {previewRows.length > 0 && (
            <>
              <div className="mb-3">
                <h6>Step 2: Map Columns to Task Fields</h6>
                <Alert variant="warning" className="mb-3">
                  <small>
                    <i className="fas fa-info-circle me-1"></i>
                    <strong>Note:</strong> "Task Name" is required. Select "None" for columns you don't want to import.
                  </small>
                </Alert>
                <Row className="g-3">
                  <Col md={6}>
                    {renderColumnSelect("taskName", "Task Name", true)}
                  </Col>
                  <Col md={6}>
                    {renderColumnSelect("taskDescription", "Description", false)}
                  </Col>
                  <Col md={6}>
                    {renderColumnSelect("assignedTo", "Assignee", false)}
                  </Col>
                  <Col md={6}>
                    {renderColumnSelect("taskStartDate", "Start Date", false)}
                  </Col>
                  <Col md={6}>
                    {renderColumnSelect("taskEndDate", "End Date", false)}
                  </Col>
                  <Col md={6}>
                    {renderColumnSelect("taskPriority", "Priority", false)}
                  </Col>
                  <Col md={6}>
                    {renderColumnSelect("taskStatus", "Status", false)}
                  </Col>
                  <Col md={6}>
                    {renderColumnSelect("taskType", "Task Type", false)}
                  </Col>
                  <Col md={6}>
                    {renderColumnSelect("stageBelong", "Stage", false)}
                  </Col>
                </Row>
              </div>

              <div className="mb-3">
                <h6>Preview (First 5 rows):</h6>
                <div className="table-responsive" style={{ maxHeight: "300px", overflowY: "auto" }}>
                  <Table size="sm" bordered>
                    <thead>
                      <tr>
                        <th>Task Name</th>
                        <th>Description</th>
                        <th>Assignee</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Task Type</th>
                        <th>Stage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.slice(1, 6).map((row, idx) => (
                        <tr key={idx}>
                          <td>{selectedColumns.taskName !== null ? row[selectedColumns.taskName] || "-" : "-"}</td>
                          <td>{selectedColumns.taskDescription !== null ? row[selectedColumns.taskDescription] || "-" : "-"}</td>
                          <td>{selectedColumns.assignedTo !== null ? row[selectedColumns.assignedTo] || "-" : "-"}</td>
                          <td>{selectedColumns.taskStartDate !== null ? row[selectedColumns.taskStartDate] || "-" : "-"}</td>
                          <td>{selectedColumns.taskEndDate !== null ? row[selectedColumns.taskEndDate] || "-" : "-"}</td>
                          <td>{selectedColumns.taskPriority !== null ? row[selectedColumns.taskPriority] || "Medium" : "Medium"}</td>
                          <td>{selectedColumns.taskStatus !== null ? row[selectedColumns.taskStatus] || "Not Started" : "Not Started"}</td>
                          <td>{selectedColumns.taskType !== null ? row[selectedColumns.taskType] || "-" : "-"}</td>
                          <td>{selectedColumns.stageBelong !== null ? row[selectedColumns.stageBelong] || "-" : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <small className="text-muted">
                  Total data rows: {previewRows.length - 1} (excluding header)
                </small>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPasteModal(false)}>
            Cancel
          </Button>
          {!previewRows.length ? (
            <Button variant="primary" onClick={handleParsePaste}>
              <i className="fas fa-search me-2"></i>
              Parse & Preview
            </Button>
          ) : (
            <Button variant="success" onClick={addTasksFromPaste}>
              <i className="fas fa-plus me-2"></i>
              Add Tasks
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TaskBatchCreator;